import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  IGroupDetail,
  useAllFakturowniaInvoices,
  useAllExpenses,
  useExpenseMutations,
  useFakturowniaInvoiceAggregates,
  useGroupFinanceContributions,
  useGroupPermissions,
} from 'core/hooks';
import { buildMoneyModel } from 'core/utils/groupMoney';
import type { IInvoiceAggregateRow } from 'core/utils/groupMoney';
import {
  exportFinanceCsv,
  downloadCsv,
  IFinanceCsvHeaders,
} from 'core/utils/exportCsv';

import { InvoicesTable } from '../../FakturowniaInvoices/InvoicesTable';
import { ExpensesTable } from '../../FakturowniaExpenses/ExpensesTable';
import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import { ContributionsSummary } from '../parts/ContributionsSummary';
import { MoneyModelGrid } from '../parts/MoneyModelGrid';
import { GroupDebtorsPanel } from '../parts/GroupDebtorsPanel';
import { TabStateView } from '../parts/TabStateView';
import { GroupColors } from '../parts/groupColors';

interface IFinancesTabProps {
  group: IGroupDetail;
}

/**
 * Group finances view — the per-currency Money_Model surface (Req 5–7).
 *
 * Data flow into the Money_Model (`buildMoneyModel`, `core/utils/groupMoney`):
 *   - contract side — `useGroupFinanceContributions(group.id)` returns the
 *     group's `agreements[]` (each carrying its `contributions` schedule).
 *     Task 8.1 removed the inline `agreements` block from the slim
 *     `GroupDetailQuery`, so this tab no longer reads it off the group; the
 *     contract-side figures (Planned / Paid / Debt) come from this lazy query
 *     instead.
 *   - invoice side — `useFakturowniaInvoiceAggregates({ groupId, direction:
 *     'issued', groupBy: 'currency' })` returns one aggregate row per currency
 *     (the `group` bucket key holds the currency code, `totalGross` the issued
 *     gross). These are mapped into the util's `IInvoiceAggregateRow[]` shape
 *     and supply the Issued figure per currency (Req 6.1–6.4).
 *
 * `buildMoneyModel` joins the two per currency — one row per currency present
 * in the active agreements, never summing across currencies — and the result
 * is rendered by `MoneyModelGrid`. The old single `groupBy: 'none'` PLN
 * aggregate and the PLN-hardcoded KPI cards are gone (Req 6.2).
 *
 * Invoice/aggregate data is retrieved through the existing Fakturownia hooks
 * scoped by `groupId` (Req 7.1). G7 (canonical `payment → agreement → group`
 * filter) is recorded as an assumption: the GraphQL signature is unchanged
 * whether or not the backend migration has shipped, so this tab works against
 * either resolver (Req 7.3).
 *
 * The whole surface is wrapped in `TabStateView` so a failed finance query
 * shows a distinct, retryable error state vs. a genuine empty state (Req 14).
 * A finance export control (CSV) is shown only when the operator may view
 * finance data, and the export entrypoint also refuses to run without that
 * permission (Req 17.5–17.6).
 */
export const FinancesTab = memo(({ group }: IFinancesTabProps) => {
  const { t } = useTranslation();
  const groupId = group.id;
  const { canExportFinance } = useGroupPermissions();

  // Contract side (Planned / Paid / Debt) — formerly inline on the group.
  const {
    agreements: financeAgreements,
    loading: contributionsLoading,
    error: contributionsError,
    refetch: refetchContributions,
  } = useGroupFinanceContributions(groupId);

  // Invoice side (Issued) — per-currency aggregates, no hardcoded currency.
  const {
    rows: revenueAgg,
    loading: aggLoading,
    error: aggError,
    refetch: refetchAgg,
  } = useFakturowniaInvoiceAggregates({
    groupId,
    direction: 'issued',
    groupBy: 'currency',
  });

  const {
    data: invoices,
    fetching: invoicesFetching,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useAllFakturowniaInvoices({
    groupId,
    direction: 'issued',
  });
  const {
    data: expenses,
    fetching: expensesFetching,
    error: expensesError,
    refetch: refetchExpenses,
  } = useAllExpenses({
    groupId,
  });
  const { setStatus } = useExpenseMutations();

  const onSetExpenseStatus = useCallback(
    (id: string, status: string) => {
      setStatus(id, status).then((ok) => {
        if (ok) refetchExpenses();
      });
    },
    [setStatus, refetchExpenses],
  );

  // Per-currency Money_Model: contract side joined with invoice side, never
  // summing across currencies. The aggregate rows are mapped into the util's
  // structural `IInvoiceAggregateRow` shape (`group` = currency, `totalGross`).
  const moneyModelRows = useMemo(() => {
    const invoiceAggByCurrency: IInvoiceAggregateRow[] = revenueAgg.map(
      (row) => ({ group: row.group, totalGross: row.totalGross }),
    );
    return buildMoneyModel(financeAgreements, invoiceAggByCurrency);
  }, [financeAgreements, revenueAgg]);

  // Localized export headers (Req 17.4). The four Money_Model stages reuse the
  // shared `pages.groupDetail.money.*` keys so the export, KpiStrip and
  // MoneyModelGrid all read one term per stage (Req 5.4); currency/overdue/
  // upcoming reuse the existing finances column keys.
  const exportHeaders: IFinanceCsvHeaders = useMemo(
    () => ({
      currency: t('pages.groupDetail.finances.col.currency'),
      planned: t('pages.groupDetail.money.planned'),
      issued: t('pages.groupDetail.money.issued'),
      paid: t('pages.groupDetail.money.paid'),
      debt: t('pages.groupDetail.money.debt'),
      overdue: t('pages.groupDetail.finances.col.overdue'),
      upcoming: t('pages.groupDetail.finances.col.upcoming'),
    }),
    [t],
  );

  const handleExportFinance = useCallback(() => {
    // Double-gated: the control is hidden without permission, and the
    // entrypoint returns `null` (produces no file) if invoked without it.
    const csv = exportFinanceCsv(
      moneyModelRows,
      exportHeaders,
      canExportFinance,
    );
    if (csv === null) {
      return;
    }
    downloadCsv(csv, `group-${groupId}-finances.csv`);
  }, [moneyModelRows, exportHeaders, canExportFinance, groupId]);

  const handleRetry = useCallback(() => {
    refetchContributions();
    refetchAgg();
    refetchInvoices();
    refetchExpenses();
  }, [refetchContributions, refetchAgg, refetchInvoices, refetchExpenses]);

  const financeError =
    contributionsError || aggError || invoicesError || expensesError;
  const hasAnyData =
    moneyModelRows.length > 0 || invoices.length > 0 || expenses.length > 0;
  const isFetching =
    contributionsLoading || aggLoading || invoicesFetching || expensesFetching;

  return (
    <TabStateView
      loading={isFetching && !hasAnyData}
      error={financeError}
      isEmpty={!hasAnyData}
      onRetry={handleRetry}
    >
      <S.DimWhenFetching fetching={isFetching}>
        {canExportFinance && (
          <Toolbar>
            <ExportButton type="button" onClick={handleExportFinance}>
              {t('pages.groupDetail.finances.export')}
            </ExportButton>
          </Toolbar>
        )}

        <MoneyModelGrid rows={moneyModelRows} />

        <ContributionsSummary agreements={financeAgreements} />

        {canExportFinance ? <GroupDebtorsPanel groupId={groupId} /> : null}

        <S.Card>
          <S.CardTitle>
            {t('pages.groupDetail.finances.invoicesSection')}
          </S.CardTitle>
          {invoices.length === 0 ? (
            <S.SubTitle>
              {t('pages.groupDetail.finances.noInvoices')}
            </S.SubTitle>
          ) : (
            <InvoicesTable rows={invoices} />
          )}
        </S.Card>

        <S.Card>
          <S.CardTitle>
            {t('pages.groupDetail.finances.expensesSection')}
          </S.CardTitle>
          {expenses.length === 0 ? (
            <S.SubTitle>
              {t('pages.groupDetail.finances.noExpenses')}
            </S.SubTitle>
          ) : (
            <ExpensesTable
              rows={expenses}
              onRowClick={() => undefined}
              onSetStatus={onSetExpenseStatus}
            />
          )}
        </S.Card>
      </S.DimWhenFetching>
    </TabStateView>
  );
});

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const ExportButton = styled.button`
  border: 1px solid ${GroupColors.accent};
  background: ${GroupColors.surface};
  color: ${GroupColors.accent};
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;

  &:hover:not(:disabled) {
    background: ${GroupColors.accent};
    color: ${GroupColors.surface};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
